if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initInlineVideos();
        this.initProgressBar();
        if (this.dataset.pdpGallery) this.initPdp();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // Shopify-hosted videos play in their frame, muted and looping (product-thumbnail.liquid).
      // The autoplay attribute starts them; this only backs it up for a frame a colour change
      // adds (product-info.js#updateMedia moves it in from a parsed document) and, under
      // reduced motion, stops them on their poster.
      initInlineVideos() {
        const list = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        if (!list) return;
        const still = window.matchMedia('(prefers-reduced-motion: reduce)');
        const sync = () =>
          list.querySelectorAll('video.product__inline-video').forEach((video) => {
            video.muted = true;
            if (still.matches) {
              if (!video.hasAttribute('autoplay')) return;
              video.removeAttribute('autoplay');
              video.pause();
              video.load(); // back to the poster
            } else if (video.paused) {
              video.play()?.catch(() => {});
            }
          });
        sync();
        new MutationObserver(sync).observe(list, { childList: true });
      }

      initProgressBar() {
        const bar = this.querySelector('.gallery-progress__bar');
        const slider = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        if (!bar || !slider) return;
        const count = slider.querySelectorAll('.product__media-item').length;
        if (count < 2) return;
        bar.style.width = `${100 / count}%`;
        slider.addEventListener(
          'scroll',
          () => {
            const max = slider.scrollWidth - slider.clientWidth;
            if (max > 0) bar.style.transform = `translateX(${(slider.scrollLeft / max) * (count - 1) * 100}%)`;
          },
          { passive: true }
        );
      }

      // NAO-97 (T4): the naomi layout's stacked gallery on phones, colour announcement and
      // gallery events. BUILD-PLAN 1.7; contract in assets/naomi-pdp.js.
      initPdp() {
        const list = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        if (!list) return;
        const items = () => Array.from(list.querySelectorAll('.product__media-item'));
        const seen = new Set();
        const viewed = (index) => {
          const total = items().length;
          if (!index || seen.has(index)) return;
          seen.add(index);
          window.NaomiPDP?.track('gallery_slide_viewed', { index, total });
        };

        // Phones show the first data-pdp-cap photos of the colour (the section's "Photos shown
        // on phones"). The last one shown is laid under an ink veil with "+N" and "View all 18
        // photos" on it (its data-pdp-more and data-pdp-more-label, drawn by pdp-gallery.css,
        // owner 2026-09-29: a corner "+N" was not obvious enough) and opens the viewer on the
        // first photo not shown, with every photo of the colour in it (pdp-lightbox.js). Capped
        // frames are display none, so their images never load and pdp-bar.js (photos(), the
        // dots) never counts them. Runs again on a colour change (the observer below, before
        // the resize keep reads the height) and on a turn past the phone breakpoint; tablet and
        // desktop show everything.
        const cap = Number(this.dataset.pdpCap) || 0;
        const capStack = () => {
          const all = items();
          const shown = cap && !this.mql.matches ? Math.min(cap, all.length) : all.length;
          const more = all.length - shown;
          // ponytail: the plural "other" form only; a cap of 3 or more never leaves one photo
          const label = (this.dataset.pdpViewAll || '').replace('[count]', all.length);
          all.forEach((item, i) => {
            item.classList.toggle('is-capped', i >= shown);
            const last = more > 0 && i === shown - 1;
            const toggle = item.querySelector('.product__media-toggle');
            if (last) {
              item.dataset.pdpMore = more;
              item.dataset.pdpMoreLabel = label;
              toggle?.setAttribute('aria-label', label);
            } else {
              delete item.dataset.pdpMore;
              delete item.dataset.pdpMoreLabel;
              toggle?.removeAttribute('aria-label');
            }
          });
        };
        capStack();
        this.mql.addEventListener('change', capStack);

        if ('IntersectionObserver' in window) {
          // stack depth: each frame counts once, when half of it is on screen
          const frames = new IntersectionObserver(
            (entries) =>
              entries.forEach((entry) => {
                if (entry.isIntersecting && !this.mql.matches) viewed(items().indexOf(entry.target) + 1);
              }),
            { threshold: 0.5 }
          );
          const observeAll = () => items().forEach((item) => frames.observe(item));
          observeAll();
          new MutationObserver(() => {
            capStack();
            observeAll();
          }).observe(list, { childList: true });

          // A colour change re-filters the stack in place (product-info.js#updateMedia). The
          // page never jumps: with scroll anchoring off (pdp-gallery.css), a stack that grows
          // or shrinks wholly above the screen moves the scroll by the same amount.
          if ('ResizeObserver' in window) {
            let height = list.offsetHeight;
            new ResizeObserver(() => {
              const next = list.offsetHeight;
              const delta = next - height;
              height = next;
              if (delta && !this.mql.matches && list.getBoundingClientRect().bottom - delta <= 0) {
                window.scrollBy(0, delta);
              }
            }).observe(list);
          }
        }

        // "Showing rose gold photos", once per colour change, in the gallery's polite status
        let colour = window.NaomiPDP?.state.options.colour;
        window.NaomiPDP?.on((state) => {
          const next = state.options.colour;
          if (!next || next === colour) return;
          colour = next;
          const region = this.elements.liveRegion;
          if (!region || !this.dataset.pdpShowing) return;
          region.setAttribute('aria-hidden', 'false');
          region.textContent = this.dataset.pdpShowing.replace('[color]', next.toLowerCase());
          clearTimeout(this.pdpAnnounce);
          this.pdpAnnounce = setTimeout(() => region.setAttribute('aria-hidden', 'true'), 2000);
        });

        this.addEventListener('click', (event) => {
          const opener = event.target.closest('.product__modal-opener');
          const item = opener?.closest('.product__media-item');
          if (item) window.NaomiPDP?.track('lightbox_opened', { index: items().indexOf(item) + 1 });
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // NAO-7: Dawn used to smooth-scroll the page back up to the variant's
          // featured image here. On this PDP that yanks the shopper away from
          // the option they just picked, so the page stays put.
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
